import { useState } from "react";
import { HeartHandshake, ShieldCheck, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PAYPAL_URL = process.env.REACT_APP_PAYPAL_DONATE_URL;

const montants = [
  { valeur: "50 $", impact: "offre une place gratuite à un atelier d'éducation fiscale ou juridique" },
  { valeur: "100 $", impact: "finance une consultation gratuite pour une personne en situation de vulnérabilité" },
  { valeur: "300 $", impact: "offre une formation complète à un entrepreneur en démarrage" },
  { valeur: "Autre", impact: "chaque montant compte, peu importe la somme" },
];

export default function Donate() {
  const [choix, setChoix] = useState(1);

  return (
    <>
      <section className="bg-espresso py-28 text-ivory md:py-36" data-testid="don-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Faire un don</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="don-title">
              Votre générosité rend le savoir <span className="italic text-primary">accessible</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Grâce à vos dons, nous finançons des bourses, du mentorat et des mesures
              d'accompagnement, et nos formations demeurent accessibles aux personnes et aux
              organisations qui en ont le plus besoin.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="don-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Choisissez votre montant</p>
            <div className="mt-8 grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2" data-testid="don-montants">
              {montants.map((m, i) => (
                <button
                  key={m.valeur}
                  onClick={() => setChoix(i)}
                  data-testid={`don-montant-${i}`}
                  className={`p-8 text-left transition-colors duration-200 ${
                    choix === i ? "bg-espresso text-ivory" : "bg-background hover:bg-card"
                  }`}
                >
                  <span className={`font-serif text-4xl font-medium ${choix === i ? "text-gold" : "text-primary"}`}>{m.valeur}</span>
                  <p className={`mt-3 text-sm leading-relaxed ${choix === i ? "text-ivory/70" : "text-foreground/60"}`}>
                    {m.impact}
                  </p>
                </button>
              ))}
            </div>

            <a
              href={PAYPAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="don-paypal-button"
              className="group mt-10 flex w-fit items-center gap-3 rounded-sm bg-primary px-10 py-5 text-base font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
            >
              <HeartHandshake size={20} />
              Donner {montants[choix].valeur !== "Autre" ? montants[choix].valeur : ""} via PayPal
              <ExternalLink size={16} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <p className="mt-4 max-w-lg text-xs leading-relaxed text-foreground/50" data-testid="don-paypal-note">
              Vous serez redirigé·e vers PayPal dans un nouvel onglet pour compléter votre don en
              toute sécurité. Aucune information bancaire n'est recueillie sur notre site.
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
            <div className="space-y-10">
              <div className="border border-border bg-card p-8" data-testid="don-securite-note">
                <ShieldCheck size={28} strokeWidth={1.5} className="text-primary" />
                <h2 className="mt-5 font-serif text-2xl font-semibold">Un don en toute confiance</h2>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/60">
                  <li>Transaction sécurisée par PayPal</li>
                  <li>Reçu de don remis sur demande — reçu non officiel, sans valeur aux fins d'impôt</li>
                  <li>Rapports annuels publics sur l'utilisation des fonds</li>
                  <li>Conseil d'administration indépendant</li>
                </ul>
              </div>
              <div data-testid="don-autres-facons">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Autres façons de donner</p>
                <p className="mt-4 text-sm leading-relaxed text-foreground/60">
                  Vous préférez le virement Interac? Écrivez-nous à{" "}
                  <span className="font-semibold text-foreground">info@fondationhora.org</span> et
                  nous vous transmettrons les instructions. Par chèque : faites-le à l'ordre de la
                  Fondation HORA et postez-le au 666, rue Sherbrooke Ouest, bureau 2100, Montréal
                  (Québec) H3A 1E7.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
